import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fetchTeamInjuries } from '../api/injuries';
import InjuryCard from './injuries/InjuryCard';
import InjuryStats from './injuries/InjuryStats';
import InjuryFilter from './injuries/InjuryFilter';
import PlayerProfile from './PlayerProfile';

const { FiAlertCircle, FiRefreshCw, FiSearch } = FiIcons;

const MOCK_PLAYERS = [
  { person: { id: 1, fullName: 'Chris Sale' }, position: { abbreviation: 'SP', type: 'Pitcher' }, jerseyNumber: '41', status: { description: '15-Day IL' }, note: 'Left elbow inflammation' },
  { person: { id: 2, fullName: 'Triston Casas' }, position: { abbreviation: '1B', type: 'Infielder' }, jerseyNumber: '36', status: { description: '10-Day IL' }, note: 'Left knee sprain' },
  { person: { id: 3, fullName: 'Garrett Whitlock' }, position: { abbreviation: 'RP', type: 'Pitcher' }, jerseyNumber: '22', status: { description: '60-Day IL' }, note: 'Right hip surgery recovery' },
  { person: { id: 4, fullName: 'Masataka Yoshida' }, position: { abbreviation: 'DH', type: 'Hitter' }, jerseyNumber: '7', status: { description: 'Day-to-Day' }, note: 'Left hamstring tightness' },
  { person: { id: 5, fullName: 'Kenley Jansen' }, position: { abbreviation: 'RP', type: 'Pitcher' }, jerseyNumber: '74', status: { description: '15-Day IL' }, note: 'Right shoulder fatigue' },
  { person: { id: 6, fullName: 'Connor Wong' }, position: { abbreviation: 'C', type: 'Catcher' }, jerseyNumber: '12', status: { description: '10-Day IL' }, note: 'Right hand contusion' },
];

const InjuryTracker = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const load = async () => {
    setLoading(true);
    const data = await fetchTeamInjuries(111);
    setPlayers(data.length > 0 ? data : MOCK_PLAYERS);
    setLastUpdated(new Date());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = players;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => p.person?.fullName?.toLowerCase().includes(q));
    }
    switch (filter) {
      case 'Pitchers': return list.filter(p => p.position?.type === 'Pitcher');
      case 'Position Players': return list.filter(p => p.position?.type !== 'Pitcher');
      case '10-Day IL': return list.filter(p => p.status?.description?.includes('10'));
      case '60-Day IL': return list.filter(p => p.status?.description?.includes('60'));
      case 'Day-to-Day': return list.filter(p => p.status?.description?.toLowerCase().includes('day-to-day'));
      default: return list;
    }
  }, [players, filter, search]);

  return (
    <>
      <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.05)]">
        {/* Header */}
        <div className="p-3 sm:p-6 border-b-2 border-[#658d71] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiAlertCircle} size={20} className="text-[#c4333e]" />
            <div>
              <h3 className="text-base sm:text-xl font-bold tracking-widest text-[#d8ceb3] uppercase">
                Injury Report
              </h3>
              {lastUpdated && (
                <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest">
                  Updated {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 border border-[#658d71] text-[#a9c9b1] hover:text-white hover:border-[#a9c9b1] text-[10px] font-bold uppercase tracking-widest px-3 py-2 transition-colors self-start sm:self-auto"
          >
            <SafeIcon icon={FiRefreshCw} size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        <div className="p-3 sm:p-6 flex flex-col gap-4">
          {/* Stats Summary */}
          {!loading && players.length > 0 && <InjuryStats players={players} />}

          {/* Search + Filter */}
          <div className="flex flex-col gap-2">
            <div className="relative">
              <SafeIcon icon={FiSearch} size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#658d71]" />
              <input
                type="text"
                placeholder="Search player..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full bg-[rgba(0,0,0,0.2)] border border-[#658d71] text-white placeholder-[#658d71] text-xs font-bold uppercase tracking-widest pl-8 pr-3 py-2 focus:outline-none focus:border-[#a9c9b1] transition-colors"
              />
            </div>
            <InjuryFilter active={filter} onChange={setFilter} />
          </div>

          {/* Player Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <div className="w-6 h-6 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
              <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading injuries...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-[#658d71] text-xs uppercase tracking-widest">
              {players.length === 0 ? '✓ No injured players reported' : 'No players match your filter'}
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={filter + search}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
              >
                {filtered.map((player, i) => (
                  <InjuryCard
                    key={player.person?.id || i}
                    player={player}
                    onPlayerClick={setSelectedPlayer}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {selectedPlayer && (
        <PlayerProfile
          playerId={selectedPlayer.person?.id}
          playerName={selectedPlayer.person?.fullName}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </>
  );
};

export default InjuryTracker;